import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Chip,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { BusyButton } from "./shared";
import {
  getPlatformFinanceChannel,
  getPlatformFinanceCommissionsEligibility,
  getPlatformFinanceCommissionsMonthReport,
  getPlatformFinanceCommissionsMyResults,
  postPlatformFinanceCommissionsPayoutsClose,
  putPlatformFinanceChannelPlans,
  putPlatformFinanceChannelPayouts,
  putPlatformFinanceCommissionsEligibility,
} from "../../../api/client";
import {
  FINANCE_KPI_ACCENT,
  FinanceAlertStack,
  FinanceEmptyState,
  FinanceKpiCard,
  FinanceKpiGrid,
  FinanceLoading,
  FinancePanelIntro,
  FinanceSection,
  FinanceTableShell,
  formatBrlFromCents,
  formatCompetenciaLabel,
  formatFinanceDateBr,
  formatNum,
} from "./FinanceUi";
import { financeAiCfg } from "./FinanceMetricAi";
import { FINANCE_GLOSS, FINANCE_LABEL } from "./FinanceCopy";
import { SalesTab, TeamRulesTab } from "./FinanceCommissionsTeam";
import {
  exportCommissionMonthReportPdfs,
  exportCommissionPersonReportPdf,
} from "./financeCommissionReportPdf";
import {
  FIELD_LIMITS,
  clampInt,
  inputToPct,
  pctToInput,
  sanitizeIntTyping,
  sanitizePctTyping,
} from "./financeFormFields";

const VIEW_ALIASES = {
  attributions: "sales",
  rules: "channel-rules",
};

const VIEW_INTRO = {
  sales: {
    title: () => FINANCE_LABEL.teamSales,
    description: () => FINANCE_GLOSS.teamSalesIntro,
  },
  "team-rules": {
    title: () => FINANCE_LABEL.teamRules,
    description: () => FINANCE_GLOSS.teamRulesIntro,
  },
  "channel-rules": {
    title: () => FINANCE_LABEL.resultRules,
    description: () => FINANCE_GLOSS.channelRulesIntro,
  },
  portfolio: {
    title: () => FINANCE_LABEL.channelPortfolio,
    description: () => FINANCE_GLOSS.channelPortfolioIntro,
  },
  "month-report": {
    title: () => FINANCE_LABEL.monthReport,
    description: () => FINANCE_GLOSS.monthReportIntro,
  },
};

const ATTR_ROLE_LABEL = {
  closer: "Closer",
  indicator: "Indicador",
  owner: "Responsável",
  leader_on_closer: "Gestor (closer)",
  leader_on_indicator: "Gestor (indicador)",
};

export default function FinanceCommissionsPanel({ month, year: yearProp, view: viewProp }) {
  const year =
    yearProp != null && Number.isFinite(Number(yearProp))
      ? Number(yearProp)
      : Number(String(month || "").slice(0, 4)) || new Date().getFullYear();
  const yearMonth = month || `${year}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const rawView = VIEW_ALIASES[viewProp] || viewProp;
  const view = VIEW_INTRO[rawView] ? rawView : "sales";
  const intro = VIEW_INTRO[view];
  const introDescription =
    typeof intro.description === "function" ? intro.description() : intro.description;

  return (
    <Stack spacing={0}>
      <FinancePanelIntro
        title={intro.title()}
        description={introDescription}
        ai={financeAiCfg({
          metric: "finance_commissions_overview",
          name: intro.title(),
          value: { year, view, yearMonth },
          human: `Competência ${formatFinanceDateBr(yearMonth)}`,
          definition: FINANCE_GLOSS.resultSpeaks,
        })}
      />
      {view === "sales" ? <SalesTab yearMonth={yearMonth} /> : null}
      {view === "team-rules" ? <TeamRulesTab /> : null}
      {view === "channel-rules" ? <RulesTab yearMonth={yearMonth} /> : null}
      {view === "portfolio" ? <PortfolioTab yearMonth={yearMonth} /> : null}
      {view === "month-report" ? <MonthReportTab yearMonth={yearMonth} /> : null}
    </Stack>
  );
}

function RulesTab({ yearMonth }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansUi, setPlansUi] = useState({});
  const [defaults, setDefaults] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [poolCap, setPoolCap] = useState(0.55);

  const planKey = (p, idx) => String(p.id ?? `new-${idx}`);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [channel, elig] = await Promise.all([
        getPlatformFinanceChannel({ month: yearMonth }),
        getPlatformFinanceCommissionsEligibility(),
      ]);
      const list = channel?.plans || [];
      setPlans(list);
      const ui = {};
      list.forEach((p, idx) => {
        ui[planKey(p, idx)] = {
          name: p.name ?? "",
          revshare: pctToInput(p.revshare_pct),
          months: p.revshare_months == null ? "" : String(p.revshare_months),
          royalty: pctToInput(p.royalty_pct),
          growth: pctToInput(p.growth_bonus_pct),
        };
      });
      setPlansUi(ui);
      setTracks(channel?.tracks || elig?.tracks || []);
      setDefaults(elig?.defaults || channel?.eligibility_defaults || []);
      setPoolCap(Number(elig?.pool_cap_pct ?? channel?.pool_cap_pct ?? 0.55));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível carregar as réguas.");
    } finally {
      setLoading(false);
    }
  }, [yearMonth]);

  useEffect(() => {
    load();
  }, [load]);

  const plansErrors = useMemo(() => {
    const errs = {};
    plans.forEach((p, idx) => {
      const key = planKey(p, idx);
      const ui = plansUi[key] || {};
      const row = {};
      if (!String(ui.name || "").trim()) row.name = "Nome obrigatório.";
      if (String(ui.name || "").length > 80) row.name = "Máx. 80 caracteres.";
      const rev = inputToPct(ui.revshare, {
        maxPct: FIELD_LIMITS.channelParticipationPctMax,
        allowEmpty: false,
      });
      if (rev == null) row.revshare = `0–${FIELD_LIMITS.channelParticipationPctMax}%.`;
      if (ui.months !== "" && ui.months != null) {
        const m = clampInt(ui.months, {
          min: 1,
          max: FIELD_LIMITS.channelWindowMonthsMax,
          fallback: NaN,
        });
        if (!Number.isFinite(m)) {
          row.months = `Vazio = recorrente, ou 1–${FIELD_LIMITS.channelWindowMonthsMax}.`;
        }
      }
      const roy = inputToPct(ui.royalty, {
        maxPct: FIELD_LIMITS.channelRoyaltyPctMax,
        allowEmpty: false,
      });
      if (roy == null) row.royalty = `0–${FIELD_LIMITS.channelRoyaltyPctMax}%.`;
      const gr = inputToPct(ui.growth, {
        maxPct: FIELD_LIMITS.channelGrowthPctMax,
        allowEmpty: false,
      });
      if (gr == null) row.growth = `0–${FIELD_LIMITS.channelGrowthPctMax}%.`;
      if (Object.keys(row).length) errs[key] = row;
    });
    return errs;
  }, [plans, plansUi]);

  const hasPlanErrors = Object.keys(plansErrors).length > 0;

  const savePlans = async () => {
    if (hasPlanErrors) {
      setMsg({ ok: false, text: "Corrija os campos destacados nas réguas." });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      const items = plans.map((p, idx) => {
        const ui = plansUi[planKey(p, idx)] || {};
        return {
          ...p,
          name: String(ui.name || "").trim().slice(0, 80),
          revshare_pct: inputToPct(ui.revshare, {
            maxPct: FIELD_LIMITS.channelParticipationPctMax,
            allowEmpty: false,
          }),
          revshare_months:
            ui.months === "" || ui.months == null
              ? null
              : clampInt(ui.months, {
                  min: 1,
                  max: FIELD_LIMITS.channelWindowMonthsMax,
                  fallback: null,
                }),
          royalty_pct: inputToPct(ui.royalty, {
            maxPct: FIELD_LIMITS.channelRoyaltyPctMax,
            allowEmpty: false,
          }),
          growth_bonus_pct: inputToPct(ui.growth, {
            maxPct: FIELD_LIMITS.channelGrowthPctMax,
            allowEmpty: false,
          }),
        };
      });
      await putPlatformFinanceChannelPlans({ items });
      setMsg({ ok: true, text: "Réguas de resultado salvas." });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.message || e?.message || "Falha ao salvar réguas." });
    } finally {
      setSaving(false);
    }
  };

  const saveEligibility = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await putPlatformFinanceCommissionsEligibility({ items: defaults });
      setMsg({ ok: true, text: "Elegibilidade por tipo salva." });
      await load();
    } catch (e) {
      setMsg({
        ok: false,
        text: e?.response?.data?.message || e?.message || "Falha ao salvar elegibilidade.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <FinanceLoading />;
  if (error) return <FinanceAlertStack error={error} />;

  const trackLabel = (key) => tracks.find((t) => t.key === key)?.label || key;
  const pctEnd = <InputAdornment position="end">%</InputAdornment>;

  return (
    <Stack spacing={2}>
      {msg ? <Alert severity={msg.ok ? "success" : "error"}>{msg.text}</Alert> : null}
      <Alert severity="info" variant="outlined">
        {FINANCE_GLOSS.channelRulesIntro} {FINANCE_GLOSS.gtmVsProfile} Teto do pool padrão:{" "}
        {Math.round(poolCap * 100)}% do resultado. Informe percentuais como 35 (não 0,35).
      </Alert>
      <FinanceSection
        title={FINANCE_LABEL.resultRules}
        hint={FINANCE_GLOSS.resultRules}
        ai={financeAiCfg({
          metric: "finance_comm_rules",
          name: FINANCE_LABEL.resultRules,
          value: { plans: plans.length },
          human: `${plans.length} régua(s)`,
          definition: FINANCE_GLOSS.resultRules,
        })}
      >
        <FinanceTableShell>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Régua</TableCell>
                <TableCell>Trilha</TableCell>
                <TableCell align="right">{FINANCE_LABEL.participation}</TableCell>
                <TableCell align="right">{FINANCE_LABEL.resultWindow}</TableCell>
                <TableCell align="right">Royalty</TableCell>
                <TableCell align="right">{FINANCE_LABEL.growthPrize}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((p, idx) => {
                const key = planKey(p, idx);
                const ui = plansUi[key] || {
                  name: "",
                  revshare: "",
                  months: "",
                  royalty: "",
                  growth: "",
                };
                const err = plansErrors[key] || {};
                return (
                  <TableRow key={key}>
                    <TableCell>
                      <TextField
                        size="small"
                        value={ui.name}
                        onChange={(e) =>
                          setPlansUi((prev) => ({
                            ...prev,
                            [key]: { ...ui, name: e.target.value.slice(0, 80) },
                          }))
                        }
                        error={Boolean(err.name)}
                        helperText={err.name || " "}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>{trackLabel(p.track_key)}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        value={ui.revshare}
                        onChange={(e) =>
                          setPlansUi((prev) => ({
                            ...prev,
                            [key]: {
                              ...ui,
                              revshare: sanitizePctTyping(e.target.value, {
                                maxPct: FIELD_LIMITS.channelParticipationPctMax,
                              }),
                            },
                          }))
                        }
                        onBlur={() =>
                          setPlansUi((prev) => ({
                            ...prev,
                            [key]: {
                              ...ui,
                              revshare: pctToInput(
                                inputToPct(ui.revshare, {
                                  maxPct: FIELD_LIMITS.channelParticipationPctMax,
                                  allowEmpty: false,
                                }) ?? 0
                              ),
                            },
                          }))
                        }
                        error={Boolean(err.revshare)}
                        helperText={err.revshare || " "}
                        InputProps={{ endAdornment: pctEnd }}
                        inputProps={{ inputMode: "decimal", autoComplete: "off" }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        value={ui.months}
                        placeholder="∞"
                        onChange={(e) =>
                          setPlansUi((prev) => ({
                            ...prev,
                            [key]: { ...ui, months: sanitizeIntTyping(e.target.value) },
                          }))
                        }
                        onBlur={() => {
                          if (ui.months === "") return;
                          setPlansUi((prev) => ({
                            ...prev,
                            [key]: {
                              ...ui,
                              months: String(
                                clampInt(ui.months, {
                                  min: 1,
                                  max: FIELD_LIMITS.channelWindowMonthsMax,
                                  fallback: 12,
                                })
                              ),
                            },
                          }));
                        }}
                        error={Boolean(err.months)}
                        helperText={err.months || "vazio = recorrente"}
                        inputProps={{ inputMode: "numeric", autoComplete: "off" }}
                        sx={{ width: 110 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        value={ui.royalty}
                        onChange={(e) =>
                          setPlansUi((prev) => ({
                            ...prev,
                            [key]: {
                              ...ui,
                              royalty: sanitizePctTyping(e.target.value, {
                                maxPct: FIELD_LIMITS.channelRoyaltyPctMax,
                              }),
                            },
                          }))
                        }
                        onBlur={() =>
                          setPlansUi((prev) => ({
                            ...prev,
                            [key]: {
                              ...ui,
                              royalty: pctToInput(
                                inputToPct(ui.royalty, {
                                  maxPct: FIELD_LIMITS.channelRoyaltyPctMax,
                                  allowEmpty: false,
                                }) ?? 0
                              ),
                            },
                          }))
                        }
                        error={Boolean(err.royalty)}
                        helperText={err.royalty || " "}
                        InputProps={{ endAdornment: pctEnd }}
                        inputProps={{ inputMode: "decimal", autoComplete: "off" }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        value={ui.growth}
                        onChange={(e) =>
                          setPlansUi((prev) => ({
                            ...prev,
                            [key]: {
                              ...ui,
                              growth: sanitizePctTyping(e.target.value, {
                                maxPct: FIELD_LIMITS.channelGrowthPctMax,
                              }),
                            },
                          }))
                        }
                        onBlur={() =>
                          setPlansUi((prev) => ({
                            ...prev,
                            [key]: {
                              ...ui,
                              growth: pctToInput(
                                inputToPct(ui.growth, {
                                  maxPct: FIELD_LIMITS.channelGrowthPctMax,
                                  allowEmpty: false,
                                }) ?? 0
                              ),
                            },
                          }))
                        }
                        error={Boolean(err.growth)}
                        helperText={err.growth || " "}
                        InputProps={{ endAdornment: pctEnd }}
                        inputProps={{ inputMode: "decimal", autoComplete: "off" }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </FinanceTableShell>
        <BusyButton
          variant="contained"
          loading={saving}
          disabled={hasPlanErrors}
          onClick={savePlans}
          sx={{ mt: 1.5 }}
        >
          Salvar réguas
        </BusyButton>
      </FinanceSection>

      <FinanceSection
        title="Elegibilidade por tipo de parceiro"
        hint={FINANCE_GLOSS.eligibility}
        ai={financeAiCfg({
          metric: "finance_comm_eligibility",
          name: "Elegibilidade",
          value: { types: defaults.length },
          human: `${defaults.length} tipo(s)`,
          definition: FINANCE_GLOSS.eligibility,
        })}
      >
        <FinanceTableShell>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Participação</TableCell>
                <TableCell>Prêmio</TableCell>
                <TableCell>Trilha padrão</TableCell>
                <TableCell>Orientação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {defaults.map((d, idx) => (
                <TableRow key={d.profile_type}>
                  <TableCell>{d.profile_type}</TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(d.commission_enabled)}
                          onChange={(e) => {
                            const next = [...defaults];
                            next[idx] = { ...d, commission_enabled: e.target.checked };
                            setDefaults(next);
                          }}
                          size="small"
                        />
                      }
                      label={d.commission_enabled ? "Ligado" : "Desligado"}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(d.bonus_enabled)}
                          onChange={(e) => {
                            const next = [...defaults];
                            next[idx] = { ...d, bonus_enabled: e.target.checked };
                            setDefaults(next);
                          }}
                          size="small"
                        />
                      }
                      label={d.bonus_enabled ? "Ligado" : "Desligado"}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select
                        value={d.default_gtm_track || ""}
                        onChange={(e) => {
                          const next = [...defaults];
                          next[idx] = { ...d, default_gtm_track: e.target.value };
                          setDefaults(next);
                        }}
                      >
                        {(tracks.length
                          ? tracks
                          : [
                              { key: "reseller", label: "Revenda" },
                              { key: "franchisee", label: "Franquia" },
                              { key: "finder", label: "Indicação" },
                              { key: "alliance", label: "Aliança" },
                            ]
                        ).map((t) => (
                          <MenuItem key={t.key} value={t.key}>
                            {t.label || t.key}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {d.hint}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </FinanceTableShell>
        <BusyButton variant="outlined" loading={saving} onClick={saveEligibility} sx={{ mt: 1.5 }}>
          Salvar elegibilidade
        </BusyButton>
      </FinanceSection>
    </Stack>
  );
}

function PortfolioTab({ yearMonth }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlatformFinanceChannel({ month: yearMonth });
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível carregar a carteira.");
    } finally {
      setLoading(false);
    }
  }, [yearMonth]);

  useEffect(() => {
    load();
  }, [load]);

  const savePayouts = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await putPlatformFinanceChannelPayouts({ year_month: yearMonth, status: "simulated" });
      try {
        await postPlatformFinanceCommissionsPayoutsClose({
          year_month: yearMonth,
          status: "simulated",
        });
      } catch {
        /* ledger opcional */
      }
      setMsg({ ok: true, text: "Payouts da competência gravados." });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.message || e?.message || "Falha ao gravar payouts." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <FinanceLoading />;
  if (error) return <FinanceAlertStack error={error} />;

  const items = data?.items || [];
  const totalPayout = items.reduce((s, r) => s + (Number(r.payout_cents) || 0), 0);
  const totalAttr = items.reduce((s, r) => s + (Number(r.attributed_revenue_cents) || 0), 0);
  const eligible = items.filter((r) => r.commission_eligible !== false).length;

  return (
    <Stack spacing={2}>
      {msg ? <Alert severity={msg.ok ? "success" : "error"}>{msg.text}</Alert> : null}
      <FinanceKpiGrid columns={3}>
        <FinanceKpiCard
          title={FINANCE_LABEL.attributedResult}
          value={formatBrlFromCents(totalAttr)}
          details={[yearMonth]}
          accentColor={FINANCE_KPI_ACCENT.mrr}
          ai={financeAiCfg({
            metric: "finance_comm_mrr",
            name: FINANCE_LABEL.attributedResult,
            value: { attributed_revenue_cents: totalAttr, period_ym: yearMonth },
            human: formatBrlFromCents(totalAttr),
            definition: "MRR da carteira atribuída a representantes na competência — base do payout.",
          })}
        />
        <FinanceKpiCard
          title={FINANCE_LABEL.payoutOnResult}
          value={formatBrlFromCents(totalPayout)}
          details={["Participação − royalty + prêmio"]}
          accentColor={FINANCE_KPI_ACCENT.cost}
          ai={financeAiCfg({
            metric: "finance_comm_payout",
            name: FINANCE_LABEL.payoutOnResult,
            value: { payout_cents: totalPayout, attributed_revenue_cents: totalAttr },
            human: formatBrlFromCents(totalPayout),
            definition: FINANCE_GLOSS.resultSpeaks,
          })}
        />
        <FinanceKpiCard
          title="Representantes elegíveis"
          value={formatNum(eligible, 0)}
          details={[`${items.length} ativos`]}
          accentColor={FINANCE_KPI_ACCENT.clients}
          ai={financeAiCfg({
            metric: "finance_comm_eligible",
            name: "Representantes elegíveis",
            value: { eligible, partners: items.length },
            human: `${eligible} elegível(is)`,
            definition: FINANCE_GLOSS.eligibility,
          })}
        />
      </FinanceKpiGrid>
      <FinanceSection
        title="Carteira"
        hint={`${FINANCE_GLOSS.gtmVsProfile} Degraus de volume usam MRR acumulado no ano (YTD); o payout usa o MRR do mês.`}
        ai={financeAiCfg({
          metric: "finance_comm_portfolio",
          name: "Carteira",
          value: { partners: items.length, attributed_revenue_cents: totalAttr, payout_cents: totalPayout },
          human: `${items.length} representante(s) · payout ${formatBrlFromCents(totalPayout)}`,
          definition: "Ranking por representante: resultado atribuído, participação, royalty e payout.",
        })}
      >
        {!items.length ? (
          <FinanceEmptyState title="Sem carteira nesta competência" description="Sincronize o Financeiro ou vincule workspaces a representantes." />
        ) : (
          <FinanceTableShell>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Representante</TableCell>
                  <TableCell>Trilha</TableCell>
                  <TableCell>Elegível</TableCell>
                  <TableCell align="right">{FINANCE_LABEL.attributedResult}</TableCell>
                  <TableCell align="right">Participação</TableCell>
                  <TableCell align="right">Royalty</TableCell>
                  <TableCell align="right">Payout</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.reseller_id}>
                    <TableCell>
                      {r.name}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {r.code} · {r.profile_type}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.gtm_track_label || r.gtm_track}</TableCell>
                    <TableCell>
                      {r.commission_eligible === false ? (
                        <Chip size="small" label="Não" color="default" title={r.skip_reason || ""} />
                      ) : (
                        <Chip size="small" label="Sim" color="success" variant="outlined" />
                      )}
                      {r.skip_reason ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {r.skip_reason}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">{formatBrlFromCents(r.attributed_revenue_cents)}</TableCell>
                    <TableCell align="right">{formatBrlFromCents(r.saas_share_cents)}</TableCell>
                    <TableCell align="right">{formatBrlFromCents(r.royalty_cents)}</TableCell>
                    <TableCell align="right">{formatBrlFromCents(r.payout_cents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </FinanceTableShell>
        )}
        <BusyButton variant="outlined" loading={saving} onClick={savePayouts} sx={{ mt: 1.5 }}>
          {FINANCE_LABEL.savePayouts}
        </BusyButton>
      </FinanceSection>
    </Stack>
  );
}

function MonthReportTab({ yearMonth }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [data, setData] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlatformFinanceCommissionsMonthReport({ month: yearMonth });
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível carregar o relatório.");
    } finally {
      setLoading(false);
    }
  }, [yearMonth]);

  useEffect(() => {
    load();
  }, [load]);

  const people = data?.people || [];
  const totals = data?.totals || {};

  const handleExportPerson = async (person) => {
    setExportingId(person.person_id);
    setMsg(null);
    setError(null);
    try {
      exportCommissionPersonReportPdf({ periodYm: yearMonth, person });
      setMsg(`PDF gerado: ${person.name}`);
    } catch (e) {
      setError(e?.message || "Falha ao gerar PDF.");
    } finally {
      setExportingId(null);
    }
  };

  const handleExportAll = async () => {
    if (!people.length) return;
    setExportingAll(true);
    setMsg(null);
    setError(null);
    try {
      const res = await exportCommissionMonthReportPdfs({ periodYm: yearMonth, people });
      setMsg(`${res.count} PDF(s) gerados para ${formatCompetenciaLabel(yearMonth) || yearMonth}.`);
    } catch (e) {
      setError(e?.message || "Falha ao gerar os PDFs.");
    } finally {
      setExportingAll(false);
    }
  };

  if (loading) return <FinanceLoading />;
  if (error && !data) return <FinanceAlertStack error={error} />;

  return (
    <Stack spacing={2}>
      <FinanceAlertStack error={error} success={msg} onCloseSuccess={() => setMsg(null)} />
      <Alert severity="info" variant="outlined">
        {data?.gloss || FINANCE_GLOSS.monthReportHint}
      </Alert>
      <FinanceKpiGrid columns={3}>
        <FinanceKpiCard
          title="Competência"
          value={formatCompetenciaLabel(yearMonth) || yearMonth}
          details={["Parcelas creditadas (earned)"]}
          accentColor={FINANCE_KPI_ACCENT.clients}
        />
        <FinanceKpiCard
          title="Pessoas com payout"
          value={formatNum(totals.people || 0, 0)}
          details={[`${totals.lines || 0} linha(s)`]}
          accentColor={FINANCE_KPI_ACCENT.mrr}
        />
        <FinanceKpiCard
          title="Total a pagar"
          value={formatBrlFromCents(totals.payout_cents || 0)}
          details={["Soma das parcelas do mês"]}
          accentColor={FINANCE_KPI_ACCENT.cost}
        />
      </FinanceKpiGrid>
      <FinanceSection
        title="Por pessoa"
        hint={FINANCE_GLOSS.monthReportHint}
        action={
          <BusyButton
            variant="outlined"
            size="small"
            loading={exportingAll}
            disabled={!people.length}
            onClick={handleExportAll}
          >
            {FINANCE_LABEL.exportPdfAll}
          </BusyButton>
        }
      >
        {!people.length ? (
          <FinanceEmptyState
            title="Nenhuma comissão creditada neste mês"
            description="Lance vendas do time e aguarde as parcelas da competência, ou confira outra competência."
          />
        ) : (
          <FinanceTableShell>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pessoa</TableCell>
                  <TableCell>Contrato</TableCell>
                  <TableCell align="right">Linhas</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {people.map((p) => {
                  const open = expandedId === p.person_id;
                  return (
                    <React.Fragment key={p.person_id}>
                      <TableRow
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => setExpandedId(open ? null : p.person_id)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {p.name}
                          </Typography>
                          {p.email ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {p.email}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {(p.contract_type || "—").toString().toUpperCase()}
                          {p.band_key ? ` · ${p.band_key}` : ""}
                        </TableCell>
                        <TableCell align="right">{p.items?.length || 0}</TableCell>
                        <TableCell align="right">{formatBrlFromCents(p.payout_cents)}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <BusyButton
                            size="small"
                            variant="contained"
                            loading={exportingId === p.person_id}
                            onClick={() => handleExportPerson(p)}
                          >
                            {FINANCE_LABEL.exportPdfPerson}
                          </BusyButton>
                        </TableCell>
                      </TableRow>
                      {open ? (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ bgcolor: "action.hover", py: 1.5 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Workspace</TableCell>
                                  <TableCell>Tipo</TableCell>
                                  <TableCell>Papel</TableCell>
                                  <TableCell>Parc.</TableCell>
                                  <TableCell align="right">Base</TableCell>
                                  <TableCell align="right">Payout</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(p.items || []).map((it) => (
                                  <TableRow key={it.id}>
                                    <TableCell>{it.dominio_name || it.dominio_id || "—"}</TableCell>
                                    <TableCell>
                                      {it.sale_kind === "upgrade" ? "Upgrade" : "Novo"}
                                    </TableCell>
                                    <TableCell>{it.role_label || ATTR_ROLE_LABEL[it.role] || it.role}</TableCell>
                                    <TableCell>
                                      {it.installment_index != null ? `${it.installment_index}/12` : "—"}
                                    </TableCell>
                                    <TableCell align="right">{formatBrlFromCents(it.base_cents)}</TableCell>
                                    <TableCell align="right">{formatBrlFromCents(it.payout_cents)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </FinanceTableShell>
        )}
      </FinanceSection>
    </Stack>
  );
}

export function MyResultsTab({ yearMonth }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlatformFinanceCommissionsMyResults({ month: yearMonth });
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível carregar seus resultados.");
    } finally {
      setLoading(false);
    }
  }, [yearMonth]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <FinanceLoading />;
  if (error && !data) return <FinanceAlertStack error={error} />;

  const items = data?.items || [];
  const totals = data?.totals || {};
  const person = data?.person;

  const handleExportPdf = async () => {
    if (!person) return;
    setExporting(true);
    setMsg(null);
    setError(null);
    try {
      let reportPerson = null;
      try {
        const report = await getPlatformFinanceCommissionsMonthReport({
          month: yearMonth,
          person_id: person.id,
        });
        reportPerson = (report?.people || []).find((p) => Number(p.person_id) === Number(person.id));
      } catch {
        reportPerson = null;
      }
      exportCommissionPersonReportPdf({
        periodYm: yearMonth,
        person: reportPerson || {
          person_id: person.id,
          name: person.name,
          email: person.email,
          band_key: person.band_key,
          payout_cents: totals.payout_cents || 0,
          items: items.map((it) => ({
            ...it,
            role_label: ATTR_ROLE_LABEL[it.role] || it.role,
            base_cents: it.attributed_revenue_cents,
          })),
        },
      });
      setMsg("PDF do seu demonstrativo gerado.");
    } catch (e) {
      setError(e?.message || "Falha ao gerar PDF.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <FinanceAlertStack error={error} success={msg} onCloseSuccess={() => setMsg(null)} />
      <Alert severity="info" variant="outlined">
        {data?.gloss || FINANCE_GLOSS.resultSpeaks}
      </Alert>
      {!person ? (
        <FinanceEmptyState
          title="Você ainda não está no ciclo de pessoas"
          description="Peça para o admin vincular seu usuário master em Incentivos → Pessoas. Depois o item Meus resultados aparece no menu."
        />
      ) : (
        <>
          <FinanceKpiGrid columns={3}>
            <FinanceKpiCard
              title="Você"
              value={person.name}
              details={[person.band_key || ""]}
              accentColor={FINANCE_KPI_ACCENT.clients}
            />
            <FinanceKpiCard
              title="Base das vendas"
              value={formatBrlFromCents(totals.attributed_revenue_cents || 0)}
              details={[yearMonth]}
              accentColor={FINANCE_KPI_ACCENT.mrr}
            />
            <FinanceKpiCard
              title="Sua comissão"
              value={formatBrlFromCents(totals.payout_cents || 0)}
              details={[`${totals.deals || 0} linha(s)`]}
              accentColor={FINANCE_KPI_ACCENT.cost}
            />
          </FinanceKpiGrid>
          <FinanceSection
            title="Extrato da competência"
            hint={FINANCE_GLOSS.myResultsIntro}
            action={
              <BusyButton
                variant="outlined"
                size="small"
                loading={exporting}
                disabled={!items.length}
                onClick={handleExportPdf}
              >
                {FINANCE_LABEL.exportPdfPerson}
              </BusyButton>
            }
          >
            {!items.length ? (
              <FinanceEmptyState
                title="Sem resultado nesta competência"
                description="Quando houver vendas lançadas em que você é closer, indicador ou gestor, o payout aparece aqui."
              />
            ) : (
              <FinanceTableShell>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Workspace</TableCell>
                      <TableCell>Papel</TableCell>
                      <TableCell>Parc.</TableCell>
                      <TableCell align="right">Base</TableCell>
                      <TableCell align="right">Payout</TableCell>
                      <TableCell>Obs.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((it, idx) => (
                      <TableRow key={it.id || idx}>
                        <TableCell>{it.dominio_name || it.dominio_id || "—"}</TableCell>
                        <TableCell>{ATTR_ROLE_LABEL[it.role] || it.role}</TableCell>
                        <TableCell>
                          {it.installment_index != null ? `${it.installment_index}/12` : "—"}
                        </TableCell>
                        <TableCell align="right">{formatBrlFromCents(it.attributed_revenue_cents)}</TableCell>
                        <TableCell align="right">{formatBrlFromCents(it.payout_cents)}</TableCell>
                        <TableCell>
                          {it.sale_kind ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={it.sale_kind === "upgrade" ? "Upgrade" : "Novo"}
                              sx={{ mr: 0.5 }}
                            />
                          ) : null}
                          {it.cap_applied ? (
                            <Chip size="small" label="Teto do pool" color="warning" />
                          ) : null}
                          {it.skip_reason ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {it.skip_reason}
                            </Typography>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </FinanceTableShell>
            )}
          </FinanceSection>
        </>
      )}
    </Stack>
  );
}
